import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useFlow } from 'context/FlowContext';
import HouseholdMemberTabs from 'components/storefront/HouseholdMemberTabs';
import SubscriptionMemberPanel, {
  createDefaultSubscriptionMember,
  memberSubtotalCents,
  MEAL_TYPES,
} from 'components/storefront/SubscriptionMemberPanel';
import { fetchMenu, fetchSubscriptionDeliveryOptions, submitSubscriptionCheckout } from 'services/publicFlowService';
import { preferredSlotKey, slotLabel, slotKey } from 'utils/deliverySlots';
import { useDeliveryPrefill } from 'hooks/useDeliveryPrefill';
import DeliveryAddressFields from 'components/shared/DeliveryAddressFields';

// Rough wireframe only — real household members/quiz data come from
// FlowContext (shared with the a-la-carte flow), and add-ons come from the
// same /api/menu catalog the a-la-carte page uses (mealType "Add On" — see
// constants/menuContent.js). Only the meal-count/add-on quantities themselves
// are still local-only mock state, keyed by member id, since there's no real
// subscription data model wired up yet.

export default function SubscriptionBuilderPage() {
  const {
    householdMembers,
    activeMemberId,
    setActiveMember,
    addHouseholdMember,
    removeHouseholdMember,
    updateHouseholdMember,
    customer,
    setCustomer,
    delivery,
    setDelivery,
  } = useFlow();

  const activeMember = householdMembers.find((m) => m.id === activeMemberId) || householdMembers[0];

  const [subscriptionByMember, setSubscriptionByMember] = useState({});
  const activeSubscription = subscriptionByMember[activeMember?.id] || createDefaultSubscriptionMember();

  const handleChangeSubscription = (subscription) => {
    setSubscriptionByMember((prev) => ({ ...prev, [activeMember.id]: subscription }));
  };

  // Same catalog the a-la-carte menu renders, filtered to the "Add On" meal
  // type — not a separate add-ons data source, just the normal dish list.
  const [addons, setAddons] = useState([]);
  const [addonsLoading, setAddonsLoading] = useState(true);
  const [defaultMealTargets, setDefaultMealTargets] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetchMenu({}).then(({ dishes, defaultMealTargets: defaults }) => {
      if (cancelled) return;
      setAddons((dishes || []).filter((d) => d.mealType === 'Add On'));
      setDefaultMealTargets(defaults || {});
      setAddonsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  useDeliveryPrefill(delivery, setDelivery);

  const { user } = useAuth0();
  useEffect(() => {
    if (user?.email && !customer.email?.trim()) setCustomer({ email: user.email });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const zip = String(delivery.zip || '').replace(/\D/g, '').slice(0, 5);
  const [zipStatus, setZipStatus] = useState({ state: 'idle', message: '', slots: [] });
  const [selectedSlotKey, setSelectedSlotKey] = useState('');

  useEffect(() => {
    if (zip.length < 5) {
      setZipStatus({ state: 'idle', message: '', slots: [] });
      return;
    }
    let cancelled = false;
    setZipStatus({ state: 'checking', message: '', slots: [] });
    fetchSubscriptionDeliveryOptions(zip).then((result) => {
      if (cancelled) return;
      if (!result) {
        setZipStatus({ state: 'idle', message: '', slots: [] });
      } else if (!result.supported) {
        setZipStatus({ state: 'unsupported', message: result.message, slots: [] });
      } else {
        setZipStatus({ state: 'supported', message: '', slots: result.slots || [] });
      }
    });
    return () => { cancelled = true; };
  }, [zip]);

  const selectedSlot = zipStatus.slots.find((s) => slotKey(s.day, s.time) === selectedSlotKey);

  // Like checkout: keep a still-offered pick, otherwise default to the preferred slot.
  useEffect(() => {
    if (zipStatus.slots.some((s) => slotKey(s.day, s.time) === selectedSlotKey)) return;
    setSelectedSlotKey(preferredSlotKey(zipStatus.slots));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipStatus.slots]);

  const grandTotalCents = householdMembers.reduce(
    (sum, m) => sum + memberSubtotalCents(subscriptionByMember[m.id] || createDefaultSubscriptionMember(), addons),
    0,
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const canSubmit = Boolean(
    customer.name?.trim() && customer.email?.trim() && delivery.address?.trim()
      && selectedSlot && grandTotalCents > 0 && !submitting,
  );

  const handleSubscribe = async () => {
    setSubmitError('');
    if (!canSubmit) {
      setSubmitError('Fill in your name, email, address and a delivery date, and add at least one meal first.');
      return;
    }
    setSubmitting(true);
    try {
      const members = householdMembers
        .map((m) => {
          const subscription = subscriptionByMember[m.id] || createDefaultSubscriptionMember();
          const meals = {};
          MEAL_TYPES.forEach(({ key, mealType }) => {
            const qty = subscription.meals?.[key] || 0;
            if (qty > 0) meals[mealType] = qty;
          });
          return { name: m.name, email: m.email || undefined, meals };
        })
        .filter((m) => Object.keys(m.meals).length > 0);

      const result = await submitSubscriptionCheckout({
        name: customer.name,
        email: customer.email,
        phone: delivery.phone,
        address: delivery.address,
        address2: delivery.address2,
        city: delivery.city,
        state: delivery.state,
        zip,
        deliveryDay: selectedSlot.day,
        deliveryTime: selectedSlot.time,
        members,
      });

      if (result.success && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      setSubmitError(result.error || 'Something went wrong starting checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sf-flow-page sf-sub-page">
      <h1 className="sf-sub-page__title">Build your subscription</h1>
      <p className="sf-sub-page__subtitle">
        No specific menu yet — just how many of each meal you want delivered
        each week. We&apos;ll pick the actual dishes from that week&apos;s menu
        before your deadline.
      </p>

      <section className="sf-sub-delivery">
        <h3 className="sf-sub-section__title">Deliver to</h3>
        <div className="sf-checkout__section--card">
          <DeliveryAddressFields value={delivery} onChange={setDelivery} idPrefix="subscribe" showNotes={false}>
            <input
              className="sf-input"
              placeholder="Name"
              value={customer.name || ''}
              onChange={(e) => setCustomer({ name: e.target.value })}
              required
              autoComplete="name"
              data-testid="subscribe-name"
            />
            <input
              className="sf-input"
              type="email"
              style={{ marginTop: 'var(--sf-space-md)' }}
              placeholder="Email"
              value={customer.email || ''}
              onChange={(e) => setCustomer({ email: e.target.value })}
              required
              autoComplete="email"
              data-testid="subscribe-email"
            />
          </DeliveryAddressFields>
        </div>
        {zipStatus.state === 'unsupported' && (
          <p className="sf-error sf-checkout__zip-error">{zipStatus.message}</p>
        )}
        {zipStatus.state === 'supported' && zipStatus.slots.length === 0 && (
          <p className="sf-error sf-checkout__zip-error">
            No delivery slots are configured for your area yet. Please check back soon.
          </p>
        )}
      </section>

      <section className="sf-sub-delivery">
        <div className="sf-checkout__schedule-row">
          <label className="sf-checkout__schedule-label" htmlFor="subscribe-slot">
            Deliver every week on
          </label>
          {zipStatus.slots.length > 0 ? (
            <select
              id="subscribe-slot"
              className="sf-checkout__schedule-select"
              value={selectedSlotKey}
              onChange={(e) => setSelectedSlotKey(e.target.value)}
            >
              {zipStatus.slots.map((slot) => (
                <option key={slotKey(slot.day, slot.time)} value={slotKey(slot.day, slot.time)}>
                  {slotLabel(slot)}{slot.fee > 0 ? ` · $${Number(slot.fee).toFixed(2)} delivery` : ''}
                </option>
              ))}
            </select>
          ) : (
            <span className="sf-checkout__schedule-placeholder">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 3" />
              </svg>
              {zipStatus.state === 'checking' ? 'Checking delivery times…' : 'Enter your address to see delivery times'}
            </span>
          )}
        </div>
      </section>

      {/* Tabs sit OUTSIDE the frame (not nested in it) so the frame's left/right
          border only ever spans the content below — nesting the tabs inside it
          made those borders run the tab row's full height too, overshooting the
          top corners instead of starting cleanly at the horizontal line. */}
      <div className="sf-sub-page__tabs">
        <HouseholdMemberTabs
          members={householdMembers}
          activeMemberId={activeMember?.id}
          onSelect={setActiveMember}
          onAddMember={addHouseholdMember}
          onRemoveMember={removeHouseholdMember}
          onUpdateMember={updateHouseholdMember}
        />
      </div>

      {/* Border carries straight down from the tab strip so everything below
          (meals, add-ons, subtotal) reads as "inside" the active person's tab. */}
      <div className="sf-sub-page__tabframe">
        {activeMember && (
          <SubscriptionMemberPanel
            subscription={activeSubscription}
            householdMember={activeMember}
            addons={addons}
            addonsLoading={addonsLoading}
            defaultMealTargets={defaultMealTargets}
            onChangeMember={handleChangeSubscription}
          />
        )}
      </div>

      <div className="sf-sub-page__total">
        <span>
          Weekly total ({householdMembers.length} {householdMembers.length === 1 ? 'person' : 'people'})
        </span>
        <strong>${(grandTotalCents / 100).toFixed(2)}</strong>
      </div>

      {submitError && (
        <p className="sf-sub-delivery__message sf-sub-delivery__message--error">{submitError}</p>
      )}
      <button
        type="button"
        className="sf-sub-page__submit"
        disabled={!canSubmit}
        onClick={handleSubscribe}
      >
        {submitting ? 'Starting checkout…' : 'Continue to payment setup'}
      </button>
      <p className="sf-sub-section__hint">
        No charge happens now — this just saves a card on file. You&apos;re
        charged weekly, after your edit deadline, only for what you keep.
      </p>
    </div>
  );
}
